import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  ClassResults,
  Exam,
  ExamDetail,
  ExamListParams,
  ExamMarkEntryPayload,
  ExamPublication,
} from '@/types/exam';
import type { ExamPayload } from '../exams/schema';

/**
 * Every call the exam screens make.
 *
 * No institute id in any path — the server takes it from the caller's token, so
 * there is nothing for a client to point at another institute's papers. The
 * academic year is the server's decision too, and which papers come back is
 * scoped to the caller's own allotments.
 *
 * Plain JSON throughout: a paper carries no file, unlike an activity, so none
 * of these needs the multipart Content-Type dance.
 */
export const examsApi = {
  list: (params: ExamListParams = {}) =>
    unwrap(
      apiClient.get<ApiSuccess<Exam[]>>('/institute/exams', {
        // Empty filters are dropped rather than sent as blanks — the server
        // validates the shape of what arrives, and `examType=` is not a type.
        params: {
          ...(params.classSectionUuid && { classSectionUuid: params.classSectionUuid }),
          ...(params.subjectUuid && { subjectUuid: params.subjectUuid }),
          ...(params.examType && { examType: params.examType }),
          ...(params.examDate && { examDate: params.examDate }),
          ...(params.origin && { origin: params.origin }),
          ...(params.status && { status: params.status }),
        },
      }),
    ),

  /**
   * Every subject's marks for one exam of a section the caller is class teacher
   * of. `name` absent means the section's most recent exam.
   */
  classResults: (classSectionUuid: string, name?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ClassResults>>('/institute/exams/class-results', {
        params: { classSectionUuid, ...(name && { name }) },
      }),
    ),

  /** The paper and its roster in one call — the marks sheet's only read. */
  get: (uuid: string) => unwrap(apiClient.get<ApiSuccess<ExamDetail>>(`/institute/exams/${uuid}`)),

  /** Absent `publication` means published, as every caller before drafts meant. */
  create: (payload: ExamPayload & { publication?: ExamPublication }) =>
    unwrap(apiClient.post<ApiSuccess<Exam>>('/institute/exams', payload)),

  /**
   * The same paper for several sections at once — one paper per section, set
   * together or not at all. Comes back as the papers it set.
   */
  createBatch: (
    payload: Omit<ExamPayload, 'classSectionUuid'> & {
      classSectionUuids: string[];
      publication?: ExamPublication;
    },
  ) => unwrap(apiClient.post<ApiSuccess<Exam[]>>('/institute/exams/batch', payload)),

  /** `publication: 'PUBLISHED'` releases a draft; a published paper never goes back. */
  update: (uuid: string, payload: Partial<ExamPayload> & { publication?: 'PUBLISHED' }) =>
    unwrap(apiClient.patch<ApiSuccess<Exam>>(`/institute/exams/${uuid}`, payload)),

  remove: (uuid: string) => unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/exams/${uuid}`)),

  /** Puts a deleted paper back as it was — date, timings and any marks entered. */
  restore: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<Exam>>(`/institute/exams/${uuid}/restore`)),

  /**
   * Saves the sheet whole.
   *
   * Every child in one request rather than a mark at a time: a sheet
   * half-saved looks entered and is wrong, with nobody able to say which half.
   * Comes back as the reloaded sheet, so the averages on screen are the
   * server's rather than a guess made while saving.
   */
  saveMarks: (uuid: string, entries: ExamMarkEntryPayload[]) =>
    unwrap(apiClient.put<ApiSuccess<ExamDetail>>(`/institute/exams/${uuid}/marks`, { entries })),
};
